<template>
  <div>
    <div class="d-none d-md-block">
      <v-tooltip bottom v-if="!isReadOnlyUser">
        <template v-slot:activator="{ on }">
          <v-icon medium class="mr-2" v-on="on" @click.stop="openMoveDialog(item)" data-cy="move">
            content_paste_go
          </v-icon>
        </template>
        <span>{{ $t("instructions.folder.move") }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-badge
            bordered
            overlap
            :color="item.proofs?.length > 0 ? 'primary' : 'error'"
            :content="computedItemProofLength"
            class="mr-6"
          >
            <v-icon
              medium
              v-on="on"
              @click.stop="openInstructionProofList()"
              data-cy="copy"
              >mdi-file-pdf</v-icon
            ></v-badge
          >
        </template>
        <span>{{ $t("instructions.instructionProof") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!isReadOnlyUser">
        <template v-slot:activator="{ on }">
          <v-icon
            medium
            class="mr-4"
            v-on="on"
            @click.stop="openInstructionProof()"
            data-cy="copy"
            >cloud_upload</v-icon
          >
        </template>
        <span>{{ $t("instructions.addProof") }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-icon
            medium
            class="mr-2"
            v-on="on"
            @click.stop="downloadItem(item)"
            data-cy="copy"
            >download</v-icon
          >
        </template>
        <span>{{ $t("instructions.download") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!isReadOnlyUser">
        <template v-slot:activator="{ on }">
          <v-icon
            medium
            class="mr-2"
            v-on="on"
            @click.stop="openEditDialog()"
            data-cy="edit"
          >
            edit</v-icon
          >
        </template>
        <span>{{ $t("instructions.editInstruction") }}</span>
      </v-tooltip>
      <v-tooltip bottom v-if="canDeleteHere">
        <template v-slot:activator="{ on }">
          <v-icon
            medium
            class="mr-2"
            v-on="on"
            @click.stop="openDeleteDialog(item)"
            data-cy="delete"
          >
            delete</v-icon
          >
        </template>
        <span>{{ $t("instructions.deleteInstruction") }}</span>
      </v-tooltip>
    </div>
    <div class="d-block d-md-none">
      <div class="text-center">
        <v-menu bottom :close-on-click="true">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="primary" dark v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item v-if="!isReadOnlyUser" @click.stop="openMoveDialog(item)">
              <v-list-item-title>{{ $t("instructions.folder.move") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click.stop="openInstructionProofList()">
              <v-list-item-title>{{ $t("instructions.examProofs") }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!isReadOnlyUser" @click.stop="openInstructionProof()">
              <v-list-item-title>{{ $t("instructions.addExamProof") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click.stop="downloadItem(item)">
              <v-list-item-title>{{ $t("instructions.download") }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!isReadOnlyUser" @click.stop="openEditDialog()">
              <v-list-item-title>{{ $t("instructions.editInstruction") }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="canDeleteHere" @click.stop="openDeleteDialog(item)">
              <v-list-item-title>{{ $t("general.delete") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <deleteDialog
      @triggerDelete="deleteItem"
      :item="item"
      :deleteDialog="deleteDialog"
    />
    <moveDialog
      :item="item"
      :moveDialog="moveDialog"
      :loading="moving"
      :array="instructionFolders"
      @cancel="moveDialog.open = false"
      @reload="reloadDataFunc"
      @confirm="handleMoveConfirm"
    />
    <instructionUploadProof
      ref="instructionUploadProof"
      :data="instructionUploadProofData"
      :item="item"
      @reloadData="reloadDataFunc"
    />
    <instructionProofList
      ref="instructionProofList"
      :data="instructionProofListData"
      :item="item"
      @reloadData="reloadDataFunc"
    />
    <instructionEdit
      ref="instructionEdit"
      :data="instructionEditData"
      :item="item"
      @reloadData="reloadDataFunc"
    />
  </div>
</template>

<script>
import deleteDialog from "../../_utils/dataTableHelpers/actions/deleteDialog.vue";
import moveDialog from "../../_utils/dataTableHelpers/actions/moveDialog.vue";
import instructionUploadProof from "./instructionUploadProof.vue";
import instructionProofList from "./instructionProofList.vue";
import instructionEdit from "./instructionEdit.vue";
export default {
  props: ["item"],
  components: {
    deleteDialog,
    moveDialog,
    instructionUploadProof,
    instructionProofList,
    instructionEdit,
  },
  data: () => ({
    deleteDialog: {
      open: false,
      title: "instructions.deleteTitle",
      description: "",
    },
    moveDialog: {
      open: false,
      title: "instructions.folder.move",
      description: "",
      targetId: null,
    },
    moving: false,
    instructionFolders: [],
    instructionUploadProofData: {
      open: false,
      title: "instructions.addProof",
      item: null,
      method: "new",
    },
    instructionProofListData: {
      open: false,
      title: "instructions.instructionProof",
      item: null,
      method: "new",
    },
    instructionEditData: {
      open: false,
      title: "instructions.editInstruction",
      item: null,
      method: "edit",
    },
  }),
  computed: {
    computedItemProofLength() {
      return String(this.item.proofs?.length ?? 0);
    },
  },
  methods: {
    createFormPath(item) {
      if (item) {
        return (
          "Unterweisungen/" +
          item.name +
          "_" +
          item.location +
          "." +
          item.filetype
        );
      }
    },
    async downloadItem(item) {
      //open url to download file
      window.open(await this.getDownloadItemLink(item), "_blank");
    },
    async getDownloadItemLink(item) {
      //download path
      const formPath = this.createFormPath(item);
      if (formPath) {
        return await this.$store.dispatch("getPresignedDownloadUrl", formPath);
      }
    },
    openDeleteDialog(item) {
      this.deleteDialog.open = true;
      this.deleteDialog.description = this.$t("instructions.deleteTitleLong", {
        title: item.title || item.name,
      });
    },
    deleteItem(item) {
      this.$store.dispatch("deleteInstruction", item).then(() => {
        this.reloadDataFunc();
        this.deleteDialog.open = false;
      });
    },
    reloadDataFunc() {
      this.$emit("reloadData");
    },
    async openMoveDialog(item) {
      this.moveDialog.open = true;
      this.moveDialog.description = this.$t("instructions.folder.movingInstruction", {
        name: item.title || item.name,
      });

      const result = await this.$store.dispatch("getAllInstructionFolders");
      const allFolders = Array.isArray(result) ? result : result?.data || [];
      const currentFolderId = this.$route.params.instructionFolderId;

      this.instructionFolders = allFolders.filter(
        (f) => String(f.id) !== String(currentFolderId),
      );
    },
    async handleMoveConfirm(targetId) {
      try {
        this.moving = true;
        await this.$store.dispatch("moveInstruction", {
          id: this.item.id,
          instructionFolder: targetId,
        });
        this.moveDialog.open = false;
        this.reloadDataFunc();
      } finally {
        this.moving = false;
      }
    },
    openInstructionProof() {
      this.instructionUploadProofData.open = true;
    },
    openInstructionProofList() {
      this.instructionProofListData.open = true;
    },
    openEditDialog() {
      this.instructionEditData.open = true;
    },
  },
};
</script>
